"use client";
import { cn } from "@/lib/utils";
import React, { useState, createContext, useContext, useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { IconMenu2, IconX } from "@tabler/icons-react";

interface Links {
  label: string;
  href: string;
  icon: React.JSX.Element | React.ReactNode;
  onClick?: () => void;
}

interface SidebarContextProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  animate: boolean;
}

const SidebarContext = createContext<SidebarContextProps | undefined>(
  undefined
);

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider");
  }
  return context;
};

export const SidebarProvider = ({
  children,
  open: openProp,
  setOpen: setOpenProp,
  animate = true,
}: {
  children: React.ReactNode;
  open?: boolean;
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  animate?: boolean;
}) => {
  const [openState, setOpenState] = useState(false);

  const open = openProp ?? openState;
  const setOpen = setOpenProp ?? setOpenState;

  const contextValue = useMemo(
    () => ({ open, setOpen, animate }),
    [open, setOpen, animate]
  );

  return (
    <SidebarContext.Provider value={contextValue}>
      {children}
    </SidebarContext.Provider>
  );
};

export const Sidebar = ({
  children,
  open,
  setOpen,
  animate,
}: {
  children: React.ReactNode;
  open?: boolean;
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  animate?: boolean;
}) => {
  return (
    <SidebarProvider open={open} setOpen={setOpen} animate={animate}>
      {children}
    </SidebarProvider>
  );
};

export const SidebarBody = ({ 
  className, 
  children, 
  ...props 
}: React.ComponentProps<typeof motion.div>) => {
  return (
    <>
      <DesktopSidebar className={className} {...props}>
        {children as React.ReactNode}
      </DesktopSidebar>
      <MobileSidebar className={className}>
        {children as React.ReactNode}
      </MobileSidebar>
    </>
  );
};

export const DesktopSidebar = ({
  className,
  children,
  ...props
}: React.ComponentProps<typeof motion.div>) => {
  const { open, setOpen, animate } = useSidebar();

  // Extract nested ternary for sidebar width calculation
  const expandedWidth = 240;
  const collapsedWidth = 60;
  let sidebarWidth: number;
  if (animate) {
    sidebarWidth = open ? expandedWidth : collapsedWidth;
  } else {
    sidebarWidth = expandedWidth;
  }

  return (
    <motion.div
      className={cn(
        "h-full py-4 hidden md:flex md:flex-col bg-neutral-200 dark:bg-[#0E0E0E] flex-shrink-0",
        "will-change-[width]",
        open ? "px-4" : "px-3",
        className
      )}
      initial={false}
      animate={{
        width: sidebarWidth,
      }}
      transition={{
        type: "tween",
        duration: 0.15,
        ease: "easeOut"
      }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      {...props}
    >
      {children}
    </motion.div>
  );
};

export const MobileSidebar = ({
  className,
  children,
  ...props
}: React.ComponentProps<"div">) => {
  const { open, setOpen } = useSidebar();
  return (
    <div
      className={cn(
        "h-10 px-4 py-4 flex flex-row md:hidden items-center justify-between bg-neutral-200 dark:bg-neutral-900 w-full"
      )}
      {...props}
    >
      <div className="flex justify-end z-20 w-full">
        <IconMenu2
          className="text-neutral-800 dark:text-neutral-200 cursor-pointer"
          onClick={() => setOpen(!open)}
        />
      </div>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ x: "-100%", opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: "-100%", opacity: 0 }}
            transition={{
              duration: 0.3,
              ease: "easeInOut",
            }}
            className={cn(
              "fixed h-full w-full inset-0 bg-white dark:bg-neutral-900 p-10 z-[100] flex flex-col justify-between",
              className
            )}
          >
            <button
              className="absolute right-10 top-10 z-50 text-neutral-800 dark:text-neutral-200 cursor-pointer p-1 hover:opacity-75 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-neutral-400 dark:focus:ring-neutral-600 rounded"
              onClick={() => setOpen(!open)}
              aria-label="Close sidebar"
              type="button"
            >
              <IconX />
            </button>
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const SidebarLink = ({
  link,
  className,
  active,
  ...props
}: {
  link: Links;
  className?: string;
  active?: boolean;
}) => {
  const { open, animate } = useSidebar();

  const handleClick = (e: React.MouseEvent) => {
    if (link.onClick) {
      e.preventDefault();
      link.onClick();
    }
  };

  // Extract nested ternary for display style based on animation and open state
  const openDisplayStyle = "inline-block";
  const closedDisplayStyle = "none";
  const defaultDisplayStyle = "inline-block";
  let displayStyle: string;
  if (animate) {
    displayStyle = open ? openDisplayStyle : closedDisplayStyle;
  } else {
    displayStyle = defaultDisplayStyle;
  }

  // Extract nested ternary for opacity value based on animation and open state
  const openOpacity = 1;
  const closedOpacity = 0;
  const defaultOpacity = 1;
  let opacityValue: number;
  if (animate) {
    opacityValue = open ? openOpacity : closedOpacity;
  } else {
    opacityValue = defaultOpacity;
  }

  // Extract nested ternary for alignment classes based on open state
  const openAlignmentClasses = "justify-start gap-2 px-3 py-2";
  const closedAlignmentClasses = "justify-center w-9 h-9";
  const alignmentClasses = open ? openAlignmentClasses : closedAlignmentClasses;

  // Extract nested ternary for active state classes
  const activeClasses = "bg-neutral-300 dark:bg-neutral-700/60";
  const inactiveClasses = "text-neutral-700 dark:text-neutral-200 hover:bg-neutral-300/50 dark:hover:bg-neutral-700/30";
  const activeStateClasses = active ? activeClasses : inactiveClasses;

  return (
    <a
      href={link.href}
      onClick={handleClick}
      className={cn(
        "flex items-center group/sidebar rounded-lg transition-all duration-200",
        alignmentClasses,
        activeStateClasses,
        className
      )}
      {...props}
    >
      <div className={cn(
        "flex-shrink-0 [&_svg]:h-5 [&_svg]:w-5 [&_svg]:transition-colors",
        active 
          ? "[&_svg]:!text-warning" 
          : ""
      )}>
        {link.icon}
      </div>

      <motion.span
        animate={{
          display: displayStyle,
          opacity: opacityValue,
        }}
        className={cn(
          "text-sm group-hover/sidebar:translate-x-1 transition duration-150 whitespace-pre inline-block !p-0 !m-0",
          active ? "!text-warning font-medium" : ""
        )}
      >
        {link.label}
      </motion.span>
    </a>
  );
};
